/**
 * AI Assistant — page registry hook (G1).
 *
 * A page calls `useAssistantPage(spec)` to publish what is on screen to the
 * layout-level <CopilotProvider>:
 *
 *   useAssistantPage({ page: 'applications', jobId, jobDetails, candidates, visibleCandidates,
 *                      filters, searchQuery, mainTab, viewMode, selectedCandidate,
 *                      onViewProfile, onLocate, onApplyFilter, onRefresh });
 *   useAssistantPage({ page: 'my-jobs', jobs, onOpenJob });
 *   useAssistantPage({ page: 'overview', stats });
 *
 * The latest spec is stored in the provider after every render (handlers are
 * always fresh; data changes are detected with a shallow compare so the
 * provider only re-renders when something on screen actually changed) and
 * the registration is removed on unmount. Rendered outside the provider the
 * hook is a no-op.
 *
 * Pass `null` to step aside: the page is unregistered (or never registered)
 * until a spec is passed again. A parent that embeds another registering page
 * (My Jobs → ApplicationList) must do this, because child effects run before
 * parent effects, so the parent would otherwise always end up on top.
 */
import { useEffect, useRef } from 'react';
import { useAssistantRegistry } from './CopilotContext';

let seq = 0;

export function useAssistantPage(spec) {
  const { registerPage, unregisterPage } = useAssistantRegistry();
  const idRef = useRef(null);
  if (idRef.current === null) {
    seq += 1;
    idRef.current = `page-${seq}`;
  }
  const id = idRef.current;

  // Publish after every render so handlers never go stale; `null` steps aside.
  useEffect(() => {
    if (spec === null) unregisterPage(id);
    else registerPage(id, spec || {});
  });

  // One registration per mounted page; removed when the page unmounts.
  useEffect(() => () => unregisterPage(id), [id, unregisterPage]);
}

export default useAssistantPage;
